import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { reportKey } from "@/profitability/query";
import { reportsPath } from "@/profitability/report-query";
import type { ReportCalendar } from "./types";

const calendarKey = (businessId: string, reportId: string) => [
  ...reportKey(businessId),
  "calendar",
  reportId,
];

/** The template's reporting calendar: weekly from which day, or monthly. */
export const useReportCalendar = (businessId: string, reportId: string) => {
  const api = useApi();
  return useQuery({
    queryKey: calendarKey(businessId, reportId),
    queryFn: ({ signal }) =>
      api
        .get<{
          calendar: ReportCalendar;
        }>(`${reportsPath(businessId, reportId)}/calendar`, { signal })
        .then((res) => res.data.calendar),
    retry: false,
  });
};

export const useUpdateCalendar = (businessId: string, reportId: string) => {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (calendar: ReportCalendar) =>
      api
        .patch<{
          calendar: ReportCalendar;
        }>(`${reportsPath(businessId, reportId)}/calendar`, {
          period_type: calendar.period_type,
          week_starts_on: calendar.week_starts_on,
          ...(calendar.period_type === "biweekly"
            ? { period_anchor_date: calendar.period_anchor_date ?? null }
            : {}),
          // Left out, the business's own zone stays in effect as it changes.
          ...(calendar.timezone_is_business_default
            ? {}
            : { timezone: calendar.timezone }),
        })
        .then((res) => res.data.calendar),
    onSuccess: (calendar) =>
      client.setQueryData(calendarKey(businessId, reportId), calendar),
  });
};
